import {
  Box,
  Col,
  ErrorMessage,
  FieldLabel,
  Form,
  type FormInstance,
  Row,
  SelectField,
  Space,
  Table,
  Text,
  TextInputField,
} from '@/components';
import { BulkRoleFormSchema } from '@/features/tenant-manager/models';
import { useForm, useId } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Key, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useOrganizations, useRoles } from '../../hooks';

type FormValues = z.infer<typeof BulkRoleFormSchema>;
type Props = {
  onSubmit: (values: FormValues) => void;
};
export const BulkRoleForm = forwardRef<FormInstance, Props>(
  function BulkRoleForm({ onSubmit }, ref) {
    const { t } = useTranslation();
    const { id: roleId } = useId();

    const {
      watch,
      control,
      formState: { errors },
      setValue,
      handleSubmit,
    } = useForm<FormValues>({
      defaultValues: {
        organizationIds: [],
      },
      resolver: zodResolver(BulkRoleFormSchema),
    });
    const [code, _roleId, organizationName, organizationIds] = watch([
      'code',
      'roleId',
      'organizationName',
      'organizationIds',
    ]);
    const onSelectChange = (newSelectedRowKeys: Key[]) => {
      setValue('organizationIds', newSelectedRowKeys as string[]);
    };

    const organizations = useOrganizations({
      select: (data) =>
        data
          .filter(
            (organization) =>
              organization.roleId !== roleId &&
              (!code || organization.code.includes(code)) &&
              (!organizationName ||
                organization.name
                  .toLowerCase()
                  .includes(organizationName.toLowerCase())) &&
              (!_roleId || organization.roleId === _roleId),
          )
          // Sorting process: Moves items that exactly match the code to the beginning of the list.
          .sort((a, b) => (a.code === code ? -1 : b.code === code ? 1 : 0)),
    });
    const roles = useRoles();

    const onCheck = useCallback(
      (keys: Key[]) => {
        // NOTE: checkboxのkeysは画面に表示されているものが入ってくる。つまり54Lでフィルターをしてしまうと、
        // フィルター前の値が入ってこない。そのため既存でセットされ、検索結果に含まれていないものを絞り込んで一緒にセットしてあげるようにしました。
        const alreadySelectedOrganizationIds = organizationIds.filter((id) =>
          organizations.data?.every((org) => org.id !== id),
        );
        const newSetKeys = [...alreadySelectedOrganizationIds, ...keys];
        setValue('organizationIds', newSetKeys as string[]);
      },
      [organizations.data, organizationIds, setValue],
    );

    return (
      <Form
        ref={ref}
        leavePageAlertCondition={null}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Box marginTop={16} marginBottom={16}>
              <Text text={t('message-bulk-role-caution')} />
            </Box>
          </Col>
          <Col span={12}>
            <TextInputField
              name="code"
              autocompleteKey="role.code"
              label={t('code')}
              control={control}
              errors={errors}
            />
          </Col>
          <Col span={12}>
            <SelectField
              name="roleId"
              label={t('role-name')}
              control={control}
              errors={errors}
              options={roles.data?.flatMap((role) =>
                role.id !== roleId
                  ? {
                      label: role.name,
                      value: role.id,
                    }
                  : [],
              )}
            />
          </Col>
          <Col span={24}>
            <TextInputField
              name="organizationName"
              autocompleteKey="organization.name"
              label={t('organization-name')}
              control={control}
              errors={errors}
            />
          </Col>
          <Col span={24}>
            <Form.Item
              label={
                <Space>
                  <FieldLabel text={t('target-organization')} required />
                  <ErrorMessage errors={errors} name="organizationIds" />
                </Space>
              }
            >
              <Table
                enableSticky={false}
                rowKey="id"
                rowSelection={{
                  selectedRowKeys: organizationIds,
                  onChange: onCheck,
                }}
                onRow={({ id }) => ({
                  onClick: () => {
                    const newOrganizationIds = organizationIds.includes(id)
                      ? organizationIds.filter((k) => k !== id)
                      : [...organizationIds, id];
                    onSelectChange(newOrganizationIds);
                  },
                })}
                dataSource={organizations.data}
                scroll={{ y: '360px' }}
                columns={[
                  {
                    title: t('code'),
                    key: 'code',
                    dataIndex: 'code',
                    width: '20%',
                  },
                  {
                    title: t('organization-name'),
                    key: 'name',
                    dataIndex: 'name',
                    width: '40%',
                  },
                  {
                    title: t('current-role-name'),
                    key: 'roleId',
                    dataIndex: 'roleId',
                    width: '40%',
                    render: (roleId) => (
                      <Text
                        text={roles.data?.find((d) => d.id === roleId)?.name}
                      />
                    ),
                  },
                ]}
                pagination={{
                  showSizeChanger: false,
                  pageSize: 50,
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    );
  },
);
